// Import dependencies
import { unsafeCSS, css, html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { addParametersToURL, getURLParameter, isDefined } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logDialogButton.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-dialog-button' )
export class AutomatorLogDialogButton extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'log-id' } ) accessor logID;

	@property( { type: Number, attribute: 'recipe-id' } ) accessor recipeID;

	@property( { type: Number, attribute: 'run-number' } ) accessor runNumber;

	// Create a reference to the dialog that doesn't exist yet
	$dialog = createRef();

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="button">
				<uo-button
					color="secondary"
					size="small"

					@click=${ this.handleOpen }
				>
					${ strings.details }
				</uo-button>
			</div>
		`;
	}

	/**
	 * Checks whether the dialog should be shown automatically, based on the URL parameters
	 */
	connectedCallback() {
		super.connectedCallback();

		// Open the dialog automatically on page load
		this.handleOpenAutomatically();
	}

	/**
	 * Checks if the dialog should be opened automatically
	 */
	handleOpenAutomatically() {
		// Get the URL parameters
		const dialogURLParameters = {
			// eslint-disable-next-line camelcase
			log_dialog_id: parseInt( getURLParameter( window.location.href, 'log_dialog_id' ) ),
			// eslint-disable-next-line camelcase
			log_recipe_id: parseInt( getURLParameter( window.location.href, 'log_recipe_id' ) ),
			// eslint-disable-next-line camelcase
			log_run_number: parseInt( getURLParameter( window.location.href, 'log_run_number' ) ),
		}

		// Check if it matches the data of this button
		if ( dialogURLParameters.log_dialog_id !== this.logID ) {
			return;
		}

		if ( dialogURLParameters.log_recipe_id !== this.recipeID ) {
			return;
		}

		if ( dialogURLParameters.log_run_number !== this.runNumber ) {
			return;
		}

		// If the data matches, we can assume the user is asking to open this dialog automatically
		this.createDialog();
	}

	/**
	 * Adds the required paraemeters to the URL and creates the dialog nodes
	 */
	handleOpen() {
		// Add parameters to the URL to render the dialog in the page is reloaded
		addParametersToURL(
			window.location.href,
			{
				// eslint-disable-next-line camelcase
				log_dialog_id: this.logID,
				// eslint-disable-next-line camelcase
				log_recipe_id: this.recipeID,
				// eslint-disable-next-line camelcase
				log_run_number: this.runNumber,
			},
			{
				setAsURL: true,
				refresh: false
			}
		);

		this.createDialog();
	}

	/**
	 * Defines what happens when the dialog is closed
	 */
	handleClose() {
		// Remove the parameters
		addParametersToURL(
			window.location.href,
			{
				// eslint-disable-next-line camelcase
				log_dialog_id: '',
				// eslint-disable-next-line camelcase
				log_recipe_id: '',
				// eslint-disable-next-line camelcase
				log_run_number: '',
			},
			{
				setAsURL: true,
				refresh: false
			}
		);
	}

	/**
	 * Creates the dialog with the log data
	 * It also resizes the dialog when the child component shows or hides the sidebar
	 */
	createDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					size="large"
					open

					no-header
					no-footer
					no-padding

					${ ref( this.$dialog ) }

					@uap-log-request-dialog-close=${ event => {
						// Check if the dialog reference exists
						if ( ! isDefined( this.$dialog.value ) ) {
							return;
						}

						this.$dialog.value.close();
					} }

					@uap-dialog-close=${ this.handleClose }
				>
					<uap-log
						log-id="${ this.logID }"
						recipe-id="${ this.recipeID }"
						run-number="${ this.runNumber }"

						@uap-log-sidebar-visibility-changed=${ ( event ) => {
							// Check if the dialog reference exists
							if ( ! isDefined( this.$dialog.value ) ) {
								return;
							}

							// If the sidebar is open, make the dialog bigger
							if ( event.detail.isOpen ) {
								this.$dialog.value.setAttribute( 'size', 'xlarge' );
								return;
							}

							// Otherwise, make it smaller
							this.$dialog.value.setAttribute( 'size', 'large' );
						} }
					></uap-log>
				</uo-dialog>
			`,
			$wrapper
		);
	}
}